import React, { useEffect, useState, useCallback } from 'react';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import LaunchIcon from '@mui/icons-material/Launch';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store';
import {
  fetchApprovals,
  cancelApproval,
  type DocumentApproval,
} from '../services/documentApprovalApi';
import { CreateApprovalDialog } from '../components/document-approvals/CreateApprovalDialog';

const STATUS_FILTERS = ['all', 'pending', 'approved', 'rejected', 'expired'] as const;
const PENDING_DAY_FILTERS = [
  { value: 'all', label: 'Any age' },
  { value: '1', label: '1+ day' },
  { value: '3', label: '3+ days' },
  { value: '7', label: '7+ days' },
  { value: '14', label: '14+ days' },
  { value: '30', label: '30+ days' },
] as const;

const statusColor = (s: string) => {
  switch (s) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCountWithLast(count: number | undefined, lastAt: string | null | undefined): string {
  const safeCount = count ?? 0;
  if (!safeCount) return '0';
  return `${safeCount} (${formatDate(lastAt ?? null)})`;
}

function getWorkerName(doc: DocumentApproval): string {
  const w = doc.worker;
  if (!w) return doc.workerId.slice(0, 8) + '...';
  return [w.firstName, w.prefix, w.lastName].filter(Boolean).join(' ');
}

function getDisplayedRecipientEmail(doc: DocumentApproval): string | null {
  return doc.workerEmail || doc.worker?.email || null;
}

function getPendingAgeInDays(doc: DocumentApproval): number {
  const createdAt = Date.parse(doc.createdAt);
  if (Number.isNaN(createdAt)) return 0;
  const diffMs = Date.now() - createdAt;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

const DocumentApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);

  const [approvals, setApprovals] = useState<DocumentApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pendingDaysFilter, setPendingDaysFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');

  const [createOpen, setCreateOpen] = useState(false);

  const loadApprovals = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const filters: Record<string, string> = {};
      if (statusFilter !== 'all') filters.status = statusFilter;
      const data = await fetchApprovals(accessToken, filters);
      setApprovals(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [accessToken, statusFilter]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleCancelApproval = async (id: string) => {
    try {
      await cancelApproval(accessToken, id);
      loadApprovals();
    } catch (err: any) {
      setError(err?.message || 'Failed to cancel');
    }
  };

  const filtered = approvals.filter((a) => {
    if (pendingDaysFilter !== 'all') {
      const minimumDays = Number(pendingDaysFilter);
      if (a.status !== 'pending' || getPendingAgeInDays(a) < minimumDays) {
        return false;
      }
    }

    if (!searchText) {
      return true;
    }

    const name = getWorkerName(a).toLowerCase();
    const email = (getDisplayedRecipientEmail(a) || '').toLowerCase();
    const t = a.title.toLowerCase();
    const q = searchText.toLowerCase();
    return name.includes(q) || email.includes(q) || t.includes(q);
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight={800}>
          Document Signing
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Request
        </Button>
      </Stack>

      {/* Filters */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {STATUS_FILTERS.map((s) => (
              <Chip
                key={s}
                label={s.charAt(0).toUpperCase() + s.slice(1)}
                variant={statusFilter === s ? 'filled' : 'outlined'}
                color={s === 'all' ? 'default' : (statusColor(s) as any)}
                onClick={() => setStatusFilter(s)}
                size="small"
              />
            ))}
            <Box sx={{ flex: 1 }} />
            <TextField
              select
              size="small"
              label="Pending age"
              value={pendingDaysFilter}
              onChange={(e) => setPendingDaysFilter(e.target.value)}
              sx={{ minWidth: 148 }}
            >
              {PENDING_DAY_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              placeholder="Search worker or title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Table */}
      {error && <Alert severity="error">{error}</Alert>}

      <Card variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {approvals.length === 0
                ? 'No signing requests yet. Create one to get started.'
                : 'No results match your filters.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Document</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Reminders</TableCell>
                  <TableCell>Opened</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>
                      <Typography variant="body2">{getWorkerName(a)}</Typography>
                      {getDisplayedRecipientEmail(a) && (
                        <Typography variant="caption" color="text.secondary">
                          {getDisplayedRecipientEmail(a)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                        {a.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {a.documentType.replace(/_/g, ' ')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={a.status}
                        color={statusColor(a.status) as any}
                        size="small"
                      />
                      {a.status === 'pending' && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {getPendingAgeInDays(a)} day{getPendingAgeInDays(a) === 1 ? '' : 's'} pending
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(a.createdAt)}</TableCell>
                    <TableCell>{formatDate(a.expiresAt)}</TableCell>
                    <TableCell>{formatCountWithLast(a.reminderCount, a.lastReminderAt)}</TableCell>
                    <TableCell>{formatCountWithLast(a.openedCount, a.lastOpenedAt)}</TableCell>
                    <TableCell align="right">
                        <Tooltip
                          title="Open detail page"
                          children={
                            <IconButton size="small" onClick={() => navigate(`/documents/${a.id}`)}>
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                      {a.status === 'pending' && (
                        <Tooltip
                          title="Cancel"
                          children={
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelApproval(a.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Dialogs */}
      <CreateApprovalDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={loadApprovals}
        accessToken={accessToken}
      />
    </Stack>
  );
};

export default DocumentApprovalsPage;
